import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { blue } from "@material-ui/core/colors";
import { API_BASE } from "../../AppConfig";
import { fetchTimeout } from "../../components/Common";
import LinearProgress from "@material-ui/core/LinearProgress";

const useStyles = makeStyles({
    avatar: {
        backgroundColor: blue[100],
        color: blue[600],
    },
    note: {
        whiteSpace: "nowrap",
        marginTop: "11px",
    },
});

const DOCUMENT_TYPE_MAP = {
    "Job Summary Layouts": "job_summary",
    "Plan View Layouts": "plan_view",
    "Engineering Documentation": "eng_doc",
    "Elevations and Isometrics Layouts": "elevation_iso",
    "Footing Layouts": "footing_layout",
    "Quote Layout": "quote_layout",
    "Order List": "order_list",
    "Council Report": "council_report",
    "Production Layouts": "prod_layout",
};

export default function DocumentFrame_Dialog(props) {
    const classes = useStyles();
    const { jobId, open, documentType, documentName, onClose } = props;

    const [documents, setDocuments] = useState([]);
    const [isListLoading, setIsListLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const showGenerateOptionInPopper = documentType !== "Quote Layout";

    const handleClose = () => {
        onClose(null);
    };

    const handleListItemClick = (value) => {
        onClose(value);
    };

    const formatFileNameParam = (documentType) => {
        return DOCUMENT_TYPE_MAP[documentType] ?? "";
    };

    useEffect(() => {
        const fetchFileListAsync = async () => {
            const formattedDocName = formatFileNameParam(documentType);
            const endPoint = API_BASE() + `document/${jobId}/${formattedDocName}`;

            try {
                setIsListLoading(true);
                setIsError(false);

                const res = await fetchTimeout(endPoint, {
                    method: "GET",
                });

                if (!res.ok) {
                    throw new Error(`Server responded ${res.status}`);
                }

                const data = await res.json();
                if (!Array.isArray(data?.FileNames)) {
                    throw new Error("Malformed data from server.");
                }

                setDocuments(data?.FileNames);
            } catch (err) {
                setIsError(true);
                setDocuments([]);
            } finally {
                setIsListLoading(false);
            }
        };

        if (open) fetchFileListAsync();
    }, [open, jobId, documentType]);

    return (
        <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={open}>
            {showGenerateOptionInPopper ? (
                <DialogTitle id="simple-dialog-title">Create a {documentName}...</DialogTitle>
            ) : (
                <DialogTitle id="simple-dialog-title">View saved {documentName}...</DialogTitle>
            )}
            <List>
                {showGenerateOptionInPopper && (
                    <ListItem button onClick={() => handleListItemClick("")} key={""}>
                        <ListItemAvatar>
                            <Avatar>
                                <CreateNewFolderIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={"Generate New " + documentType} />
                    </ListItem>
                )}
                <ListItem button style={{ alignItems: "start" }}>
                    <ListItemAvatar>
                        <Avatar className={classes.avatar}>
                            <FileCopyIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                        {isListLoading ? (
                            <LinearProgress style={{ marginTop: "17px" }} />
                        ) : isError ? (
                            <InputLabel id="demo-simple-select-label" className={classes.note}>
                                An error has occurred. Please try again.
                            </InputLabel>
                        ) : (
                            <>
                                {documents?.length > 0 ? (
                                    <InputLabel id="demo-simple-select-label" className={classes.note}>
                                        Load Previously Generated {documentType}...
                                    </InputLabel>
                                ) : (
                                    <InputLabel id="demo-simple-select-label" className={classes.note}>
                                        There are no saved {documentType}...
                                    </InputLabel>
                                )}

                                {documents?.map((document) => (
                                    <MenuItem
                                        key={document}
                                        value={document}
                                        style={{ fontSize: "100%", paddingLeft: "0px" }}
                                        onClick={() => handleListItemClick(document)}
                                    >
                                        {document}
                                    </MenuItem>
                                ))}
                            </>
                        )}
                    </div>
                </ListItem>
            </List>
        </Dialog>
    );
}

DocumentFrame_Dialog.propTypes = {
    jobId: PropTypes.string.isRequired,
    open: PropTypes.bool.isRequired,
    documentType: PropTypes.string.isRequired,
    documentName: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};
